import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { Point } from 'highcharts';
import { every, get, isEmpty, isNil, lowerCase, map, pickBy, upperCase } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlagCountry from 'src/components/common/FlagCountry';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import ExportDataTable from 'src/components/ExportData';
import { EXPORT_TABLE_TYPE, URL_EXPORT_MAPPING } from 'src/components/ExportData/config';
import TreemapChart from 'src/components/HighChart/Treemap';
import { TREEMAP_PALLET_COLOR } from 'src/components/HighChart/Treemap/config';
import WorldMapChart from 'src/components/HighChart/WorldMapChart';
import { WorldMapOptions } from 'src/components/HighChart/WorldMapChart/config';
import CustomTable from 'src/components/Table';
import CompanyTradeDataWhiteBox from 'src/features/CompanyTradeData/common/CompanyTradeDataWhiteBox';
import DataGuard from 'src/features/CompanyTradeData/common/DataGuard';
import FilterChart from 'src/features/CompanyTradeData/common/FilterChart';
import PopupBuyersSuppliersList from 'src/features/CompanyTradeData/common/PopupBuyersSuppliersList';
import { TradeDataUnitByFilter } from 'src/features/TradeData/Common/TradeDataUnitbyFilter';
import useCreditFlow from 'src/hooks/useCreditFlow';
import { useQueryParsedParams } from 'src/hooks/useQueryParam';
import {
  useQueryCTDPortChart,
  useQueryCTDPortList,
  useQueryCTDPortWorldMap,
} from 'src/services/queries/useQueryTradeDataWithoutHSCode';
import { LIMIT_GET_LIST } from 'src/utils/constants/config';
import { KeyFilterChartTradeDataUnitBy, TypeBuyerSupplier, TypePortData } from 'src/utils/constants/enum';
import { LIST_ACTIONS, LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import {
  getCountryByCountryCode,
  renderNumberOrDefault,
  renderTextOrDefault,
  renderValueNumberTrade,
} from 'src/utils/helpers/stringHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

const CompanyTradeDataViewByPort = () => {
  const { pooSortParams, setSort } = useTradeDataSortStore();
  const queryParams = useQueryParsedParams();
  const {
    palette: { secondary, text },
  } = useTheme();
  const [viewBuyersSuppliersList, setViewBuyersSuppliersList] = useState({
    open: false as boolean,
    type: TypeBuyerSupplier.supplier as TypeBuyerSupplier,
    portCode: undefined as string | undefined,
  });
  const { t } = useTranslation();
  const tradeDataUnitByFilter = useMemo(() => new TradeDataUnitByFilter(), []);

  const [unitFilter, setUnitFilter] = useState(tradeDataUnitByFilter.value);

  const unitFieldMapping = {
    bol: {
      field: 'bol',
      label: 'Number of B/L',
    },
    weight: {
      field: 'weight',
      label: 'Weight',
    },
    quantity: { field: 'quantity', label: 'Quantity' },
    value_usd: { field: 'value_usd', label: 'USD' },
  };

  const treemapFieldNameMapping = {
    [TypePortData.poo]: {
      name: 'port_name',
      countryCodeField: 'country_code',
    },
    [TypePortData.pod]: {
      name: 'port_name',
      countryCodeField: 'country_code',
    },
  } as const;

  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: queryParams.start_date,
    toTime: queryParams.end_date,
  } as {
    fromTime: string;
    toTime: string;
  });

  const isBuyer = queryParams.tab === 'PortsOfDelivery';
  const role = queryParams.tab === 'PortsOfDelivery' ? TypePortData.pod : TypePortData.poo;
  const chartLabelByUnitFilter = unitFieldMapping[unitFilter].label;
  const chartFieldNameByUnitFilter = unitFieldMapping[unitFilter].field;

  const [proportionTablePaging, setProportionTablePaging] = useState({
    page: 0,
    page_size: LIMIT_GET_LIST,
  });

  useEffect(() => {
    setProportionTablePaging({ page: 0, page_size: LIMIT_GET_LIST });
  }, [queryParams.tab]);

  const portRequestPayload = useMemo(() => {
    return pickBy(
      {
        ...queryParams,
        start_date: selectedRangeTime.fromTime,
        end_date: selectedRangeTime.toTime,
        keyword: queryParams.keyword,
        offset: 0,
        limit: 10,
        role: role,
      },
      (value) => !isNil(value) && value !== '',
    );
  }, [queryParams, selectedRangeTime]);

  const portListRequestPayload = useMemo(() => {
    return {
      ...portRequestPayload,
      limit: proportionTablePaging.page_size,
      offset: proportionTablePaging.page,
      sort_by: pooSortParams?.sortBy,
      order: pooSortParams?.sort,
      role: role,
    };
  }, [proportionTablePaging, pooSortParams, portRequestPayload]);

  const portChartRequestPayload = useMemo(() => {
    return {
      ...portRequestPayload,
      offset: 0,
      limit: 10,
      order: pooSortParams?.sort,
      role: role,
    };
  }, [proportionTablePaging, pooSortParams, portRequestPayload]);

  const [acceptCreditPayment, setAcceptCreditPayment] = useState(true);
  const portWorldMapResp = useQueryCTDPortWorldMap(portRequestPayload);
  const portChartResp = useQueryCTDPortChart(portChartRequestPayload);
  const portListResp = useQueryCTDPortList(portListRequestPayload);

  const checkIsEmptyDataAndAutoChangeUnit = () => {
    if (portListResp.isFetching || tradeDataUnitByFilter.isUserClickBack(unitFilter as KeyFilterChartTradeDataUnitBy))
      return;
    const isEmptyData =
      isEmpty(portListResp.data?.data) || every(portListResp.data?.data, (item) => get(item, unitFilter) === 0);
    if (isEmptyData) {
      setUnitFilter(tradeDataUnitByFilter.next());
    }
  };

  useEffect(() => {
    checkIsEmptyDataAndAutoChangeUnit();
  }, [portListResp]);

  const [shouldShowBillableDataLoading, hasEnoughCredit] = useCreditFlow({
    response: portListResp.data,
    featureInfo: {
      featureId: isBuyer
        ? LIST_FEATURES.COMPANY_PROFILE_PORTS_OF_DELIVERY
        : LIST_FEATURES.COMPANY_PROFILE_PORTS_OF_ORIGIN,
      action: LIST_ACTIONS.VIEW,
    },
    acceptCreditPayment,
    onSetAcceptCreditPayment: (value) => setAcceptCreditPayment(value),
  });

  const { name: treemapNameField, countryCodeField: treeMapCountryCodeField } = treemapFieldNameMapping[role];

  const treemapChart = useMemo(() => {
    const data = map(portChartResp?.data?.data, (item, i) => {
      return {
        name: get(item, treemapNameField, 'N/A'),
        value: Math.cbrt(get(item, `percentage_${chartFieldNameByUnitFilter}`, 0)),
        sortIndex: i,
        countryCode: get(item, treeMapCountryCodeField, ''),
        percentage: get(item, `percentage_${chartFieldNameByUnitFilter}`, 0),
        ...item,
      };
    });
    return (
      <DataGuard
        shouldRenderEmpty={() =>
          !shouldShowBillableDataLoading && !portChartResp.isFetching && isEmpty(portChartResp?.data?.data)
        }
        emptyTitle={hasEnoughCredit ? 'Trade Data Not Found' : 'Not Enough Credit'}
        emptyContent={
          hasEnoughCredit ? (
            <>
              Customs data may be restricted, incomplete, or obscured by alternative trading names or third-party
              logistics.
            </>
          ) : (
            'Can not access the data, please upgrade your plan'
          )
        }
      >
        <TreemapChart
          loading={shouldShowBillableDataLoading || portChartResp.isFetching}
          data={getArrayDefault(data)}
          dataLabelFormatter={function () {
            const point: Point = this.point;
            const flag = getEmojiFlag(get(point, 'countryCode', '')?.toLowerCase());
            return `<div style="color: ${secondary.main}; font-weight: 500; font-size: 14px">
              <span style="font-size:24px">${flag}</span> ${point.name}<br/>
            </div>`;
          }}
          tooltipFormatter={function () {
            const point: Point = this.point;
            const flag = getEmojiFlag(get(point, 'countryCode', '')?.toLowerCase());
            const value = get(point.options, chartFieldNameByUnitFilter);
            return `
              <div style="min-width: 270px">
                <span style="font-size: 24px; color: inherit; display: block; color: inherit; fontWeight: 600; margin-bottom: 5px">
                  ${point.percentage}%
                </span>
                <span style="color: inherit; display: block; color: inherit; fontWeight: 600">
                ${flag} ${point.name}
                </span>
                <span style="color: inherit; display: block; color: ${text.secondary}">${chartLabelByUnitFilter} <b style="color: #fff">
               ${renderNumberOrDefault(value)}</b>
                </span>
              </div>`;
          }}
          height={620}
          colors={TREEMAP_PALLET_COLOR}
        />
      </DataGuard>
    );
  }, [JSON.stringify(portChartResp), unitFilter, shouldShowBillableDataLoading, hasEnoughCredit]);

  const worldMapChart = useMemo(() => {
    const dataPoint = map(portWorldMapResp?.data?.data, (item) => {
      return {
        lon: item?.longitude || 0,
        lat: item?.latitude || 0,
        name: item?.country_code || '',
        port_name: item?.port_name || '',
        country_code: item?.country_code || '',
        bol: item?.bol || '',
        weight: item?.weight || '',
        quantity: item?.quantity || '',
        value_usd: item?.value_usd || '',
        dataLabels: {
          enabled: false,
        },
      };
    });
    const data = map(portWorldMapResp?.data?.data, (item) => [
      lowerCase(item?.country_code),
      get(item, chartFieldNameByUnitFilter, 0),
    ]) as Array<[string, number]>;
    return (
      <DataGuard
        shouldRenderEmpty={() =>
          !shouldShowBillableDataLoading && !portWorldMapResp.isFetching && isEmpty(portWorldMapResp?.data?.data)
        }
        emptyTitle={hasEnoughCredit ? 'Trade Data Not Found' : 'Not Enough Credit'}
        emptyContent={
          hasEnoughCredit ? (
            <>
              Customs data may be restricted, incomplete, or obscured by alternative trading names or third-party
              logistics.
            </>
          ) : (
            'Can not access the data, please upgrade your plan'
          )
        }
      >
        <WorldMapChart
          dataPoint={dataPoint}
          tooltipFormatter={function () {
            const point: Point = this.point;
            const pointType = point?.series?.options?.type || '';
            if (pointType === 'mappoint') {
              const flag = getEmojiFlag(point?.name);
              const countryName = getCountryByCountryCode(point?.name).label;
              const portName = get(point, 'port_name', '');
              const portType = upperCase(role);
              const bolNumber = get(point, 'bol', 0);
              const weight = get(point, 'weight', 0);
              const quantity = get(point, 'quantity', 0);
              const valueUSD = get(point, 'value_usd', 0);
              return `<div style="color: #000; min-width: 220px; line-height: 150%; font-size: 12px; display: flex; flex-direction: column; gap: 4px ">
              <span style="color: inherit; font-size: 14px">${portType} ${flag} ${countryName}</span>
              <span style="color: inherit">
             <span style="color: ${text.secondary} ">Port:</span> ${portName}</span>
              <span style="color: inherit">
             <span style="color: ${text.secondary} ">Bills of Lading:</span> ${renderValueNumberTrade(bolNumber)}</span>
              <span style="color: inherit">
             <span style="color: ${text.secondary} ">Weight (KG):</span> ${renderValueNumberTrade(weight)}</span>
              <span style="color: inherit">
             <span style="color: ${text.secondary} ">Quantity:</span> ${renderValueNumberTrade(quantity)}</span>
              <span style="color: inherit">
             <span style="color: ${text.secondary} ">Value (USD):</span> ${renderValueNumberTrade(valueUSD)}</span>
             </div>`;
            }
            return `${point.name}`;
          }}
          data={getArrayDefault(data)}
          isLoading={shouldShowBillableDataLoading || portWorldMapResp.isFetching}
          height={580}
          type={WorldMapOptions.company_bill_of_landing}
        />
      </DataGuard>
    );
  }, [JSON.stringify(portWorldMapResp), unitFilter, shouldShowBillableDataLoading, hasEnoughCredit]);

  const proportionTableConfigColumn: ConfigCell<ITradeDataByProportionalPort>[] = [
    {
      disablePadding: false,
      id: 'port_name',
      disableSort: true,
      label: !isBuyer ? t('trade_data.port_origin.table.port_name') : t('trade_data.port_delivery.table.port_name'),
      cell: (data: ITradeDataByProportionalPort) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={data?.country_code || ''} />
            <Typography variant="body2" color="text.secondary">
              {renderTextOrDefault(data.port_name)}
            </Typography>
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={1} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography variant="body2" color="text.secondary">
          {formatValueUSD(data.value_usd)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'suppliers_list',
      label: 'Suppliers List',
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography
          component={'span'}
          onClick={() =>
            setViewBuyersSuppliersList({
              open: true,
              type: TypeBuyerSupplier.supplier,
              portCode: data.port_locode,
            })
          }
          variant="body2"
          color={'secondary.main'}
        >
          {renderValueNumberTrade(data.suppliers)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'buyers_list',
      label: 'Buyers List',
      cell: (data: ITradeDataByProportionalPort) => (
        <Typography
          component={'span'}
          onClick={() =>
            setViewBuyersSuppliersList({
              open: true,
              type: TypeBuyerSupplier.buyer,
              portCode: data.port_locode,
            })
          }
          variant="body2"
          color={'secondary.main'}
        >
          {renderValueNumberTrade(data.buyers)}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <PopupBuyersSuppliersList
        portCode={viewBuyersSuppliersList.portCode}
        payload={portRequestPayload}
        type={viewBuyersSuppliersList.type}
        open={viewBuyersSuppliersList.open}
        handleOpenChange={(open) => setViewBuyersSuppliersList((prev) => ({ ...prev, open: open }))}
      />
      <CompanyTradeDataWhiteBox>
        <FilterChart
          title={`Top 10 ${isBuyer ? 'Ports of Delivery' : 'Ports of Origin'}`}
          selectedRangeTime={selectedRangeTime}
          onSelectTimeRange={setSelectedRangeTime}
          selectedUnitBy={unitFilter}
          onSelectUnitBy={(value) => setUnitFilter(value as KeyFilterChartTradeDataUnitBy)}
        />
        {treemapChart}
      </CompanyTradeDataWhiteBox>
      <CompanyTradeDataWhiteBox>
        <Typography variant="h5">{isBuyer ? 'Ports of Delivery' : 'Ports of Origin'} World Map</Typography>
        {worldMapChart}
      </CompanyTradeDataWhiteBox>
      <CompanyTradeDataWhiteBox>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            {upperCase(role)} Trade Data of{' '}
            <Box component={'span'} color={secondary.main}>
              {queryParams?.keyword}
            </Box>
          </Typography>
          <ExportDataTable
            fileName={isBuyer ? 'ctd-ports-of-delivery' : 'ctd-ports-of-origin'}
            featureId={
              isBuyer ? LIST_FEATURES.COMPANY_PROFILE_PORTS_OF_DELIVERY : LIST_FEATURES.COMPANY_PROFILE_PORTS_OF_ORIGIN
            }
            endpoint={get(URL_EXPORT_MAPPING, isBuyer ? EXPORT_TABLE_TYPE.CTD_POD : EXPORT_TABLE_TYPE.CTD_POO)}
            paramsExport={portListRequestPayload}
            totalRecordsAvailable={portListResp.data?.total_records || 0}
          />
        </Stack>

        <CustomTable
          handleSort={(data) => {
            setSort('pooSortParams', data);
          }}
          maxRow={50}
          onChangePage={(value) => {
            setProportionTablePaging({ ...proportionTablePaging, page: value });
          }}
          onChangePageSize={(value) => setProportionTablePaging({ page: 0, page_size: value })}
          loading={shouldShowBillableDataLoading || portListResp.isFetching}
          page={proportionTablePaging.page}
          pageSize={proportionTablePaging.page_size}
          totalRecords={portListResp.data?.total_records}
          dataKey={'id'}
          data={portListResp.data?.data ?? []}
          configColumn={proportionTableConfigColumn}
        />
      </CompanyTradeDataWhiteBox>
    </>
  );
};

export default CompanyTradeDataViewByPort;
